/**
 * Dashboard unit system — the shared grid vocabulary every site-dashboard card
 * is laid out against.
 *
 * A "unit" is one column of the dashboard grid. Card footprints are declared in
 * units rather than in pixels or breakpoints, so a card states its width once
 * and keeps it wherever it is used. The column count answers to the width of
 * the stage the grid is handed, not to the viewport: the context rail is 340px
 * and an operator can collapse it, so window width does not predict how much
 * room the cards actually have. Container queries read the stage directly,
 * which is what lets a span stay correct on both sides of a rail toggle.
 *
 * The grid stops widening at MAX_UNITS columns of UNIT_MAX_WIDTH_PX. A relay
 * card is a carousel above a schedule list; past that width it carries no more
 * information and only spreads the same content further apart, so surplus stage
 * width is left as margin instead of inflating the cards.
 *
 * Contains:
 * - UNIT_MIN_WIDTH_PX, UNIT_MAX_WIDTH_PX, UNIT_GAP_PX — a unit's bounds
 * - MAX_UNITS — the widest the grid ever gets
 * - unitContainerQuery — the container query that holds from a column count up
 * - unitLadderSx — column-count declarations for a section grid
 * - spanUnitsSx — a card's width in units, clamped to the columns that exist
 *
 * Returns plain JS — no JSX.
 */

/** Narrowest a relay card stays readable at, and so the floor for one unit. */
export const UNIT_MIN_WIDTH_PX = 350;

/** Widest a unit grows before surplus stage width is left as margin. */
export const UNIT_MAX_WIDTH_PX = 530;

/** Inter-card gutter. Matches the `gap: 2` the section grid renders with. */
export const UNIT_GAP_PX = 16;

/** Column ceiling: 1280px of stage reaches 2 units, 1920px reaches 4. */
export const MAX_UNITS = 4;

/** The container every dashboard section establishes for its own cards. */
export const UNIT_CONTAINER_NAME = 'dashboard';

/**
 * Stage width at which `columns` columns of `unitWidth` first fit side by side.
 *
 * @param {number} columns   - how many columns must fit.
 * @param {number} unitWidth - the width each column is being held to.
 * @returns {number} Stage width in px, gutters included.
 */
function unitLadderWidth(columns, unitWidth) {
  return columns * unitWidth + (columns - 1) * UNIT_GAP_PX;
}

/**
 * Container query matching every stage wide enough for `columns` columns.
 *
 * @param {number} columns     - column count the query should hold from.
 * @param {number} [unitWidth] - width each column is held to; defaults to the
 *   unit floor, which is what makes the query "this many columns fit".
 * @returns {string} An `@container` at-rule usable as an MUI `sx` key.
 */
export function unitContainerQuery(columns, unitWidth = UNIT_MIN_WIDTH_PX) {
  return `@container ${UNIT_CONTAINER_NAME} (min-width: ${unitLadderWidth(columns, unitWidth)}px)`;
}

/**
 * Column-count declarations for a section grid, from one column up to the
 * ceiling, plus the step where the columns stop growing.
 *
 * @returns {object} MUI `sx` fragment owning only `gridTemplateColumns`.
 */
export function unitLadderSx() {
  const ladder = { gridTemplateColumns: 'minmax(0, 1fr)' };

  for (let columns = 2; columns <= MAX_UNITS; columns += 1) {
    ladder[unitContainerQuery(columns)] = {
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    };
  }

  ladder[unitContainerQuery(MAX_UNITS, UNIT_MAX_WIDTH_PX)] = {
    gridTemplateColumns: `repeat(${MAX_UNITS}, minmax(0, ${UNIT_MAX_WIDTH_PX}px))`,
  };

  return ladder;
}

/**
 * A card's width in units. A card never spans more columns than the grid has,
 * so a multi-unit card falls back to a single unit on a stage too narrow to
 * hold its full footprint.
 *
 * @param {number} units - requested width in units.
 * @returns {object} MUI `sx` fragment owning only `gridColumn`.
 */
export function spanUnitsSx(units) {
  const clamped = Math.min(Math.max(units, 1), MAX_UNITS);
  if (clamped === 1) return { gridColumn: 'span 1' };

  return {
    gridColumn: 'span 1',
    [unitContainerQuery(clamped)]: { gridColumn: `span ${clamped}` },
  };
}
